import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { localDateStr } from '../utils/date'
import { COMPANY } from '../constants/company'
import { fetchClients, upsertClient } from '../utils/clients'
import useIsMobile from '../hooks/useIsMobile'

/** 공급자 고정 정보 — constants/company.js 공용 */
const SUPPLIER = COMPANY

const GREEN = '#1e8a4e'
const MIN_ROWS = 12 // 인쇄 시 빈 줄 포함 최소 행 수

const emptyItem = () => ({ month:'', day:'', name:'', spec:'', qty:'', unit_price:'', supply:'', note:'' })

const num = v => {
  const n = Number(String(v ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}
const fmt = v => (num(v) === 0 ? '' : num(v).toLocaleString('ko-KR'))

/**
 * 품목 1줄의 공급가액/세액
 * - 공급가액(supply)을 직접 입력하면 그 값 사용 (수량·단가 없이도 가능)
 * - 직접 입력이 없으면 수량×단가 자동 계산, 수량 미입력 시 1로 간주
 */
function calcItem(it) {
  const manual = String(it.supply ?? '').trim()
  let supply
  if (manual !== '') {
    supply = num(manual)
  } else {
    const price = num(it.unit_price)
    const qtyRaw = String(it.qty ?? '').trim()
    const qty = qtyRaw === '' ? (price > 0 ? 1 : 0) : num(it.qty)
    supply = qty * price
  }
  const tax = Math.round(supply * 0.1)
  return { supply, tax }
}

/* ——— 초록 양식 공용 스타일 ——— */
const bd = `1px solid ${GREEN}`
const bdBold = `2px solid ${GREEN}`
const thS = { border:bd, padding:'0 1mm', fontSize:'8.5pt', fontWeight:700, color:GREEN, textAlign:'center', whiteSpace:'nowrap', background:'#f3faf5', lineHeight:1.15 }
const tdS = { border:bd, padding:'0 1.2mm', fontSize:'9pt', color:'#1a1916', lineHeight:1.15 }
const lblS = { ...thS, width:'17mm' }
/** 양식 칸 안에 들어가는 투명 입력 필드 */
const cellInput = {
  width:'100%', border:'none', outline:'none', background:'transparent',
  fontFamily:'inherit', fontSize:'inherit', fontWeight:'inherit', color:'inherit',
  textAlign:'inherit', padding:0, boxSizing:'border-box',
}

function calcTotals(items, misu) {
  let supplyTotal = 0, taxTotal = 0
  for (const it of items) {
    const { supply, tax } = calcItem(it)
    supplyTotal += supply
    taxTotal += tax
  }
  return { supplyTotal, taxTotal, grandTotal: supplyTotal + taxTotal + num(misu) }
}

export default function Invoice({ profile }) {
  const [list, setList] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [showDetail, setShowDetail] = useState(null)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState([])
  const isMobile = useIsMobile()

  useEffect(() => { fetchList(); fetchClients().then(setClients) }, [])

  /** 거래처 선택 → 공급받는자 자동 입력 */
  function pickClient(id) {
    const c = clients.find(x => x.id === id)
    if (!c) return
    setForm(f => ({ ...f, buyer_name: c.name, buyer_addr: c.addr || '', buyer_tel: c.tel || '', buyer_fax: c.fax || '' }))
  }

  async function fetchList() {
    const { data, error } = await supabase.from('invoices').select('*').order('created_at', { ascending: false })
    if (error) console.error('invoices 조회:', error.message)
    if (data) setList(data)
  }

  const padItems = arr => {
    const items = arr.map(it => ({ ...it }))
    while (items.length < MIN_ROWS) items.push(emptyItem())
    return items
  }

  function openNewModal() {
    setEditingId(null)
    setForm({
      trade_date: localDateStr(),
      buyer_name: '', buyer_addr: '', buyer_tel: '', buyer_fax: '',
      receiver: '', misu: '',
      items: padItems([]),
    })
    setShowModal(true)
  }

  function openEditModal(inv) {
    setEditingId(inv.id)
    setForm({
      trade_date: inv.trade_date,
      buyer_name: inv.buyer_name || '',
      buyer_addr: inv.buyer_addr || '',
      buyer_tel: inv.buyer_tel || '',
      buyer_fax: inv.buyer_fax || '',
      receiver: inv.receiver || '',
      misu: inv.misu || '',
      items: padItems(Array.isArray(inv.items) ? inv.items : []),
    })
    setShowDetail(null)
    setShowModal(true)
  }

  function setItem(idx, patch) {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }))
  }

  function addItemRow() {
    setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))
  }

  function clearItemRow(idx) {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? emptyItem() : it) }))
  }

  async function submitInvoice() {
    if (!form.buyer_name) { alert('거래처(공급받는자) 상호를 입력하세요'); return }
    const [, mm, dd] = (form.trade_date || localDateStr()).split('-')
    const validItems = form.items
      .filter(it => it.name || num(it.qty) || num(it.unit_price) || num(it.supply))
      .map(it => ({
        ...it,
        month: String(it.month || '').trim() || String(Number(mm)),
        day: String(it.day || '').trim() || String(Number(dd)),
      }))
    if (!validItems.length) { alert('품목을 1개 이상 입력하세요'); return }
    setLoading(true)
    const { supplyTotal, taxTotal, grandTotal } = calcTotals(validItems, form.misu)
    const payload = {
      trade_date: form.trade_date || localDateStr(),
      buyer_name: form.buyer_name,
      buyer_addr: form.buyer_addr,
      buyer_tel: form.buyer_tel,
      buyer_fax: form.buyer_fax,
      receiver: form.receiver,
      misu: num(form.misu),
      items: validItems,
      supply_total: supplyTotal,
      tax_total: taxTotal,
      grand_total: grandTotal,
      created_by: profile.id,
      created_by_name: profile.name,
    }
    const q = editingId
      ? supabase.from('invoices').update(payload).eq('id', editingId)
      : supabase.from('invoices').insert(payload)
    const { error } = await q
    if (error) {
      alert('저장 실패: ' + error.message + '\n\nSupabase에 invoices 테이블이 있는지 확인하세요 (supabase/sql/invoices.sql 실행)')
      setLoading(false)
      return
    }
    // 새 거래처면 자동 등록 (실패해도 무시)
    await upsertClient({ name: form.buyer_name, addr: form.buyer_addr, tel: form.buyer_tel, fax: form.buyer_fax })
    fetchClients().then(setClients)
    setShowModal(false)
    fetchList()
    setLoading(false)
  }

  async function deleteInvoice(id) {
    if (!window.confirm('이 거래명세표를 삭제할까요?')) return
    const { error } = await supabase.from('invoices').delete().eq('id', id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    setShowDetail(null)
    fetchList()
  }

  const filtered = list.filter(inv => {
    const q = search.toLowerCase()
    if (!q) return true
    return inv.buyer_name?.toLowerCase().includes(q) || inv.trade_date?.includes(q)
  })

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }

  const formTotals = form ? calcTotals(form.items, form.misu) : null

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>거래명세표</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>작성 · 저장 · 인쇄 (공급자: {SUPPLIER.name})</div>
        </div>
        <button onClick={openNewModal}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
          + 명세표 작성
        </button>
      </div>

      {/* 검색 */}
      <div style={{ marginBottom:20 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="거래처, 날짜 검색..."
          style={inputStyle} />
      </div>

      {/* 모바일: 카드 목록 */}
      {isMobile ? (
        filtered.length === 0 ? (
          <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, textAlign:'center', padding:40, color:'#a09e99', fontSize:13 }}>
            작성된 명세표가 없습니다
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(inv => (
              <div key={inv.id} onClick={() => setShowDetail(inv)}
                style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:14, cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, gap:8 }}>
                  <span style={{ fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{inv.buyer_name}</span>
                  <span style={{ fontSize:11, color:'#a09e99', flexShrink:0 }}>{inv.trade_date}</span>
                </div>
                <div style={{ fontSize:12, color:'#6b6860', marginBottom:6 }}>
                  {inv.items?.[0]?.name}{inv.items?.length > 1 ? ` 외 ${inv.items.length - 1}건` : ''}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                  <span style={{ fontSize:11, color:'#a09e99' }}>
                    공급가 {num(inv.supply_total).toLocaleString()} · 세액 {num(inv.tax_total).toLocaleString()}
                  </span>
                  <span style={{ fontSize:15, fontWeight:700, color:'#2d5a3d' }}>₩ {num(inv.grand_total).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['거래일자','거래처','품목','공급가액','세액','합계금액','작성자',''].map((h, i) => (
                <th key={i} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#a09e99' }}>작성된 명세표가 없습니다</td></tr>
            ) : filtered.map(inv => (
              <tr key={inv.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                <td style={{ padding:'12px 14px', whiteSpace:'nowrap' }}>{inv.trade_date}</td>
                <td style={{ padding:'12px 14px', fontWeight:600 }}>{inv.buyer_name}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {inv.items?.[0]?.name}{inv.items?.length > 1 ? ` 외 ${inv.items.length - 1}건` : ''}
                </td>
                <td style={{ padding:'12px 14px', textAlign:'right', whiteSpace:'nowrap' }}>{num(inv.supply_total).toLocaleString()}</td>
                <td style={{ padding:'12px 14px', textAlign:'right', whiteSpace:'nowrap' }}>{num(inv.tax_total).toLocaleString()}</td>
                <td style={{ padding:'12px 14px', textAlign:'right', fontWeight:700, whiteSpace:'nowrap' }}>{num(inv.grand_total).toLocaleString()}</td>
                <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860' }}>{inv.created_by_name}</td>
                <td style={{ padding:'12px 14px' }}>
                  <button onClick={() => setShowDetail(inv)}
                    style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>
                    보기/인쇄
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* 작성/수정 모달 */}
      {showModal && form && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:820, maxWidth:'96vw', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>{editingId ? '거래명세표 수정' : '거래명세표 작성'}</span>
              <button onClick={() => setShowModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:16, overflowX:'auto' }}>
              {/* 거래처 불러오기 */}
              {clients.length > 0 && (
                <div style={{ maxWidth:'197mm', margin:'0 auto 10px' }}>
                  <select value="" onChange={e => pickClient(e.target.value)}
                    style={{ padding:'8px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', cursor:'pointer', maxWidth:'100%' }}>
                    <option value="">📇 거래처 불러오기 (선택 시 자동 입력)</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {/* ——— 양식 그대로 편집 ——— */}
              <div style={{ width:'197mm', margin:'0 auto', fontFamily:'Noto Sans KR, sans-serif', background:'#fff' }}>
                {/* 제목 줄 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, tableLayout:'fixed' }}>
                  <tbody>
                    <tr style={{ height:'12mm' }}>
                      <td style={{ ...tdS, width:'30mm', textAlign:'center', padding:0 }}>
                        <div style={{ fontSize:'7.5pt', color:GREEN, fontWeight:700, borderBottom:bd, padding:'0.5mm 0' }}>거 래 일 자</div>
                        <input type="date" value={form.trade_date} onChange={e => setForm({...form, trade_date:e.target.value})}
                          style={{ ...cellInput, textAlign:'center', fontSize:'8.5pt', padding:'1mm 0' }} />
                      </td>
                      <td style={{ ...tdS, textAlign:'center' }}>
                        <span style={{ fontSize:'16pt', fontWeight:800, color:GREEN, letterSpacing:'0.3em' }}>거 래 명 세 서</span>
                        <span style={{ fontSize:'8pt', color:GREEN, marginLeft:'1mm' }}>(공급자용)</span>
                      </td>
                      <td style={{ ...tdS, width:'16mm', textAlign:'center', fontSize:'8.5pt' }}>1 / 1</td>
                    </tr>
                  </tbody>
                </table>

                {/* 공급받는자 / 공급자 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
                  <tbody>
                    <tr style={{ height:'8mm' }}>
                      <td rowSpan={4} style={{ ...tdS, width:'6mm', textAlign:'center', color:GREEN, fontWeight:700, fontSize:'7.5pt', padding:'0 0.5mm' }}>
                        공<br/>급<br/>받<br/>는<br/>자
                      </td>
                      <td style={{ ...lblS, fontSize:'7.5pt' }}>상 호<br/>(법인명)</td>
                      <td style={{ ...tdS, width:'45mm', textAlign:'center', fontWeight:700, fontSize:'10.5pt' }}>
                        <input value={form.buyer_name} onChange={e => setForm({...form, buyer_name:e.target.value})}
                          placeholder="거래처 상호 *" style={cellInput} />
                      </td>
                      <td style={{ ...lblS, width:'9mm' }}>귀하</td>
                      <td rowSpan={4} style={{ ...tdS, width:'6mm', textAlign:'center', color:GREEN, fontWeight:700, fontSize:'7.5pt', padding:'0 0.5mm' }}>
                        공<br/><br/>급<br/><br/>자
                      </td>
                      <td style={{ ...lblS }}>등록번호</td>
                      <td colSpan={3} style={{ ...tdS, padding:0 }}>
                        <div style={{ display:'flex', height:'100%', alignItems:'stretch' }}>
                          {SUPPLIER.regNo.replace(/-/g, '').split('').map((d, i) => (
                            <span key={i} style={{
                              flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:'10pt', fontWeight:700,
                              borderLeft: i === 0 ? 'none' : bd,
                            }}>{d}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={{ ...lblS, fontSize:'7.5pt' }}>사 업 장<br/>주 소</td>
                      <td colSpan={2} style={{ ...tdS, fontSize:'8.5pt' }}>
                        <input value={form.buyer_addr} onChange={e => setForm({...form, buyer_addr:e.target.value})}
                          placeholder="사업장 주소" style={cellInput} />
                      </td>
                      <td style={{ ...lblS, fontSize:'7.5pt' }}>상 호<br/>(법인명)</td>
                      <td style={{ ...tdS, width:'38mm', textAlign:'center', fontWeight:700, fontSize:'9.5pt' }}>{SUPPLIER.name}</td>
                      <td style={{ ...lblS, width:'8mm' }}>성<br/>명</td>
                      <td style={{ ...tdS, width:'24mm', textAlign:'center', fontWeight:600 }}>{SUPPLIER.ceo}</td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={lblS}>전화번호</td>
                      <td colSpan={2} style={{ ...tdS, padding:0 }}>
                        <div style={{ display:'flex', alignItems:'stretch', height:'100%' }}>
                          <input value={form.buyer_tel} onChange={e => setForm({...form, buyer_tel:e.target.value})}
                            placeholder="전화" style={{ ...cellInput, flex:1, padding:'0 1.2mm', fontSize:'8.5pt' }} />
                          <span style={{ ...thS, borderTop:'none', borderBottom:'none', width:'6mm', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'7.5pt' }}>팩<br/>스</span>
                          <input value={form.buyer_fax} onChange={e => setForm({...form, buyer_fax:e.target.value})}
                            placeholder="팩스" style={{ ...cellInput, flex:1, padding:'0 1.2mm', fontSize:'8.5pt' }} />
                        </div>
                      </td>
                      <td style={{ ...lblS, fontSize:'7.5pt' }}>사 업 장<br/>주 소</td>
                      <td colSpan={3} style={{ ...tdS, fontSize:'8.5pt' }}>{SUPPLIER.addr}</td>
                    </tr>
                    <tr style={{ height:'8mm' }}>
                      <td style={lblS}>합계금액</td>
                      <td colSpan={2} style={{ ...tdS, textAlign:'center', fontWeight:800, fontSize:'12pt' }}>
                        ₩ {formTotals.grandTotal.toLocaleString()}
                      </td>
                      <td style={lblS}>전 화</td>
                      <td colSpan={3} style={{ ...tdS, padding:0 }}>
                        <div style={{ display:'flex', alignItems:'stretch', height:'100%' }}>
                          <span style={{ flex:1, display:'flex', alignItems:'center', padding:'0 1.2mm', fontSize:'8.5pt' }}>{SUPPLIER.tel}</span>
                          <span style={{ ...thS, borderTop:'none', borderBottom:'none', width:'6mm', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'7.5pt' }}>팩<br/>스</span>
                          <span style={{ flex:1, display:'flex', alignItems:'center', padding:'0 1.2mm', fontSize:'8.5pt' }}>{SUPPLIER.fax}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* 품목 — 칸을 바로 클릭해서 입력 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
                  <thead>
                    <tr style={{ height:'6mm' }}>
                      <th style={{ ...thS, width:'7mm' }}>월</th>
                      <th style={{ ...thS, width:'7mm' }}>일</th>
                      <th style={thS}>품 목</th>
                      <th style={{ ...thS, width:'22mm' }}>규 격</th>
                      <th style={{ ...thS, width:'12mm' }}>수 량</th>
                      <th style={{ ...thS, width:'20mm' }}>단 가</th>
                      <th style={{ ...thS, width:'25mm' }}>공급가액</th>
                      <th style={{ ...thS, width:'20mm' }}>세 액</th>
                      <th style={{ ...thS, width:'16mm' }}>비 고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const { supply, tax } = calcItem(it)
                      const manualSupply = String(it.supply ?? '').trim()
                      const hasContent = it.name || it.spec || it.note || String(it.qty).trim() || String(it.unit_price).trim() || manualSupply
                      const cellTd = { ...tdS, fontSize:'8.5pt' }
                      return (
                        <tr key={i} style={{ height:'6mm' }}>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.month} onChange={e => setItem(i, { month:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.day} onChange={e => setItem(i, { day:e.target.value })} style={cellInput} /></td>
                          <td style={cellTd}><input value={it.name} onChange={e => setItem(i, { name:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'center' }}><input value={it.spec} onChange={e => setItem(i, { spec:e.target.value })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'right' }}><input value={it.qty} onChange={e => setItem(i, { qty:e.target.value, supply:'' })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'right' }}><input value={it.unit_price} onChange={e => setItem(i, { unit_price:e.target.value, supply:'' })} style={cellInput} /></td>
                          <td style={{ ...cellTd, textAlign:'right' }}>
                            <input value={manualSupply !== '' ? it.supply : (supply ? supply.toLocaleString() : '')}
                              onChange={e => setItem(i, { supply:e.target.value })}
                              placeholder="" style={cellInput} />
                          </td>
                          <td style={{ ...cellTd, textAlign:'right', background:'#fbfdfb', color:'#6b6860' }}>{tax ? tax.toLocaleString() : ''}</td>
                          <td style={{ ...cellTd, position:'relative' }}>
                            <input value={it.note} onChange={e => setItem(i, { note:e.target.value })} style={cellInput} />
                            {hasContent && (
                              <button type="button" onClick={() => clearItemRow(i)} title="줄 비우기"
                                style={{ position:'absolute', right:1, top:'50%', transform:'translateY(-50%)', border:'none', background:'transparent', color:'#b83030', cursor:'pointer', fontSize:10, padding:'0 2px', lineHeight:1 }}>✕</button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {/* 하단 합계 */}
                <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
                  <tbody>
                    <tr style={{ height:'9mm' }}>
                      <td style={{ ...lblS, width:'14mm', fontSize:'7.5pt' }}>공 급<br/>가 액</td>
                      <td style={{ ...tdS, textAlign:'right', fontWeight:600, width:'27mm' }}>{formTotals.supplyTotal.toLocaleString()}</td>
                      <td style={{ ...lblS, width:'11mm' }}>세액</td>
                      <td style={{ ...tdS, textAlign:'right', fontWeight:600, width:'22mm' }}>{formTotals.taxTotal.toLocaleString()}</td>
                      <td style={{ ...lblS, width:'13mm' }}>미수금</td>
                      <td style={{ ...tdS, textAlign:'right', fontWeight:600, width:'22mm' }}>
                        <input value={form.misu} onChange={e => setForm({...form, misu:e.target.value})} placeholder="0" style={cellInput} />
                      </td>
                      <td style={{ ...lblS, width:'13mm', fontSize:'7.5pt' }}>합 계<br/>금 액</td>
                      <td style={{ ...tdS, textAlign:'right', fontWeight:800, fontSize:'10.5pt', width:'27mm' }}>{formTotals.grandTotal.toLocaleString()}</td>
                      <td style={{ ...lblS, width:'12mm' }}>인수자</td>
                      <td style={{ ...tdS, textAlign:'center', fontWeight:600 }}>
                        <input value={form.receiver} onChange={e => setForm({...form, receiver:e.target.value})} style={cellInput} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', maxWidth:'197mm', margin:'10px auto 0' }}>
                <span style={{ fontSize:11, color:'#a09e99' }}>
                  칸을 클릭해서 바로 입력 · 수량·단가 없이 공급가액만 입력해도 됨 · 월/일 비우면 거래일자로 자동 입력
                </span>
                <button type="button" onClick={addItemRow}
                  style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, flexShrink:0 }}>
                  + 줄 추가
                </button>
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitInvoice} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '저장 중...' : (editingId ? '수정하기' : '저장하기')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 보기/인쇄 모달 */}
      {showDetail && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <style>{`
            @page { size: A4 portrait; margin: 6mm; }
            @media print {
              body * { visibility: hidden !important; }
              .invoice-print, .invoice-print * { visibility: visible !important; }
              .invoice-print { position: fixed !important; left: 0; top: 0; margin: 0 !important; padding: 0 !important; }
            }
          `}</style>
          <div style={{ background:'#fff', borderRadius:14, width:880, maxWidth:'96vw', maxHeight:'92vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:14, fontWeight:700 }}>{showDetail.trade_date} · {showDetail.buyer_name}</span>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => openEditModal(showDetail)}
                  style={{ padding:'6px 14px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:12 }}>✏️ 수정</button>
                <button onClick={() => deleteInvoice(showDetail.id)}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:12 }}>🗑️ 삭제</button>
                <button onClick={() => window.print()}
                  style={{ padding:'6px 14px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>🖨️ 인쇄</button>
                <button onClick={() => setShowDetail(null)}
                  style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
              </div>
            </div>
            <div style={{ padding:16, overflowX:'auto' }}>
              <PrintSheet inv={showDetail} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * 초록 양식 거래명세서 (공급자용) — 인쇄 대상
 * A4 세로 용지의 절반(상단)에 딱 맞도록 모든 치수를 mm 단위로 고정.
 * 전체: 너비 197mm × 높이 약 133mm (A4 절반 148.5mm 안에 여유 포함)
 */
function PrintSheet({ inv }) {
  const items = Array.isArray(inv.items) ? inv.items : []
  const rows = [...items]
  while (rows.length < MIN_ROWS) rows.push(null)

  const bd = `1px solid ${GREEN}`
  const bdBold = `2px solid ${GREEN}`
  const th = { border:bd, padding:'0 1mm', fontSize:'8.5pt', fontWeight:700, color:GREEN, textAlign:'center', whiteSpace:'nowrap', background:'#f3faf5', lineHeight:1.15 }
  const td = { border:bd, padding:'0 1.2mm', fontSize:'9pt', color:'#1a1916', lineHeight:1.15 }
  const lbl = { ...th, width:'17mm' }

  const regDigits = SUPPLIER.regNo.replace(/-/g, '').split('')

  return (
    <div className="invoice-print" style={{ width:'197mm', margin:'0 auto', fontFamily:'Noto Sans KR, sans-serif', background:'#fff' }}>
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, tableLayout:'fixed' }}>
        <tbody>
          {/* 제목 줄 (12mm) */}
          <tr style={{ height:'12mm' }}>
            <td style={{ ...td, width:'30mm', textAlign:'center', padding:0 }}>
              <div style={{ fontSize:'7.5pt', color:GREEN, fontWeight:700, borderBottom:bd, padding:'0.5mm 0' }}>거 래 일 자</div>
              <div style={{ padding:'1mm 0', fontSize:'9pt' }}>{inv.trade_date}</div>
            </td>
            <td style={{ ...td, textAlign:'center' }}>
              <span style={{ fontSize:'16pt', fontWeight:800, color:GREEN, letterSpacing:'0.3em' }}>거 래 명 세 서</span>
              <span style={{ fontSize:'8pt', color:GREEN, marginLeft:'1mm' }}>(공급자용)</span>
            </td>
            <td style={{ ...td, width:'16mm', textAlign:'center', fontSize:'8.5pt' }}>1 / 1</td>
          </tr>
        </tbody>
      </table>

      {/* 공급받는자 / 공급자 (8mm × 4줄 = 32mm) */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
        <tbody>
          <tr style={{ height:'8mm' }}>
            {/* 좌측: 공급받는자 */}
            <td rowSpan={4} style={{ ...td, width:'6mm', textAlign:'center', color:GREEN, fontWeight:700, fontSize:'7.5pt', padding:'0 0.5mm' }}>
              공<br/>급<br/>받<br/>는<br/>자
            </td>
            <td style={{ ...lbl, fontSize:'7.5pt' }}>상 호<br/>(법인명)</td>
            <td style={{ ...td, width:'45mm', textAlign:'center', fontWeight:700, fontSize:'10.5pt' }}>{inv.buyer_name}</td>
            <td style={{ ...lbl, width:'9mm' }}>귀하</td>
            {/* 우측: 공급자 */}
            <td rowSpan={4} style={{ ...td, width:'6mm', textAlign:'center', color:GREEN, fontWeight:700, fontSize:'7.5pt', padding:'0 0.5mm' }}>
              공<br/><br/>급<br/><br/>자
            </td>
            <td style={{ ...lbl }}>등록번호</td>
            <td colSpan={3} style={{ ...td, padding:0 }}>
              <div style={{ display:'flex', height:'100%', alignItems:'stretch' }}>
                {regDigits.map((d, i) => (
                  <span key={i} style={{
                    flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'10pt', fontWeight:700,
                    borderLeft: i === 0 ? 'none' : bd,
                  }}>{d}</span>
                ))}
              </div>
            </td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={{ ...lbl, fontSize:'7.5pt' }}>사 업 장<br/>주 소</td>
            <td colSpan={2} style={{ ...td, fontSize:'8.5pt' }}>{inv.buyer_addr}</td>
            <td style={{ ...lbl, fontSize:'7.5pt' }}>상 호<br/>(법인명)</td>
            <td style={{ ...td, width:'38mm', textAlign:'center', fontWeight:700, fontSize:'9.5pt' }}>{SUPPLIER.name}</td>
            <td style={{ ...lbl, width:'8mm' }}>성<br/>명</td>
            <td style={{ ...td, width:'24mm', textAlign:'center', fontWeight:600 }}>{SUPPLIER.ceo}</td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={lbl}>전화번호</td>
            <td colSpan={2} style={{ ...td, padding:0 }}>
              <div style={{ display:'flex', alignItems:'stretch', height:'100%' }}>
                <span style={{ flex:1, display:'flex', alignItems:'center', padding:'0 1.2mm', fontSize:'8.5pt' }}>{inv.buyer_tel}</span>
                <span style={{ ...th, borderTop:'none', borderBottom:'none', width:'6mm', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'7.5pt' }}>팩<br/>스</span>
                <span style={{ flex:1, display:'flex', alignItems:'center', padding:'0 1.2mm', fontSize:'8.5pt' }}>{inv.buyer_fax}</span>
              </div>
            </td>
            <td style={{ ...lbl, fontSize:'7.5pt' }}>사 업 장<br/>주 소</td>
            <td colSpan={3} style={{ ...td, fontSize:'8.5pt' }}>{SUPPLIER.addr}</td>
          </tr>
          <tr style={{ height:'8mm' }}>
            <td style={lbl}>합계금액</td>
            <td colSpan={2} style={{ ...td, textAlign:'center', fontWeight:800, fontSize:'12pt' }}>
              ₩ {num(inv.grand_total).toLocaleString()}
            </td>
            <td style={lbl}>전 화</td>
            <td colSpan={3} style={{ ...td, padding:0 }}>
              <div style={{ display:'flex', alignItems:'stretch', height:'100%' }}>
                <span style={{ flex:1, display:'flex', alignItems:'center', padding:'0 1.2mm', fontSize:'8.5pt' }}>{SUPPLIER.tel}</span>
                <span style={{ ...th, borderTop:'none', borderBottom:'none', width:'6mm', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'7.5pt' }}>팩<br/>스</span>
                <span style={{ flex:1, display:'flex', alignItems:'center', padding:'0 1.2mm', fontSize:'8.5pt' }}>{SUPPLIER.fax}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* 품목 테이블 (헤더 6mm + 12줄 × 6mm = 78mm) */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
        <thead>
          <tr style={{ height:'6mm' }}>
            <th style={{ ...th, width:'7mm' }}>월</th>
            <th style={{ ...th, width:'7mm' }}>일</th>
            <th style={th}>품 목</th>
            <th style={{ ...th, width:'22mm' }}>규 격</th>
            <th style={{ ...th, width:'12mm' }}>수 량</th>
            <th style={{ ...th, width:'20mm' }}>단 가</th>
            <th style={{ ...th, width:'25mm' }}>공급가액</th>
            <th style={{ ...th, width:'20mm' }}>세 액</th>
            <th style={{ ...th, width:'16mm' }}>비 고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => {
            const c = it ? calcItem(it) : null
            return (
              <tr key={i} style={{ height:'6mm' }}>
                <td style={{ ...td, textAlign:'center', fontSize:'8.5pt' }}>{it?.month}</td>
                <td style={{ ...td, textAlign:'center', fontSize:'8.5pt' }}>{it?.day}</td>
                <td style={{ ...td, fontSize:'8.5pt', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>{it?.name}</td>
                <td style={{ ...td, textAlign:'center', fontSize:'8.5pt' }}>{it?.spec}</td>
                <td style={{ ...td, textAlign:'right', fontSize:'8.5pt' }}>{it ? fmt(it.qty) : ''}</td>
                <td style={{ ...td, textAlign:'right', fontSize:'8.5pt' }}>{it ? fmt(it.unit_price) : ''}</td>
                <td style={{ ...td, textAlign:'right', fontSize:'8.5pt' }}>{c ? fmt(c.supply) : ''}</td>
                <td style={{ ...td, textAlign:'right', fontSize:'8.5pt' }}>{c ? fmt(c.tax) : ''}</td>
                <td style={{ ...td, fontSize:'8.5pt' }}>{it?.note}</td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* 하단 합계 (9mm) */}
      <table style={{ borderCollapse:'collapse', width:'100%', border:bdBold, borderTop:'none', tableLayout:'fixed' }}>
        <tbody>
          <tr style={{ height:'9mm' }}>
            <td style={{ ...lbl, width:'14mm', fontSize:'7.5pt' }}>공 급<br/>가 액</td>
            <td style={{ ...td, textAlign:'right', fontWeight:600, width:'27mm' }}>{num(inv.supply_total).toLocaleString()}</td>
            <td style={{ ...lbl, width:'11mm' }}>세액</td>
            <td style={{ ...td, textAlign:'right', fontWeight:600, width:'22mm' }}>{num(inv.tax_total).toLocaleString()}</td>
            <td style={{ ...lbl, width:'13mm' }}>미수금</td>
            <td style={{ ...td, textAlign:'right', fontWeight:600, width:'22mm' }}>{num(inv.misu).toLocaleString()}</td>
            <td style={{ ...lbl, width:'13mm', fontSize:'7.5pt' }}>합 계<br/>금 액</td>
            <td style={{ ...td, textAlign:'right', fontWeight:800, fontSize:'10.5pt', width:'27mm' }}>{num(inv.grand_total).toLocaleString()}</td>
            <td style={{ ...lbl, width:'12mm' }}>인수자</td>
            <td style={{ ...td, textAlign:'center', fontWeight:600 }}>{inv.receiver}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
